import { useCallback, useState, useEffect } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import './plaid-link.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUniversity } from '@fortawesome/free-solid-svg-icons';
import { inject } from 'mobx-react';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import { usePlaidLinkToken } from './hooks/usePlaidLinkToken';

// Internal component that only mounts after we have a link token, thus safely calling the Plaid hook.
const PlaidLinkFlowLauncher = ({ token, onSuccess, onAfterOpen }) => {
  const { open, ready } = usePlaidLink({ token, onSuccess });

  useEffect(() => {
    if (ready) {
      open();
      if (onAfterOpen) onAfterOpen();
    }
  }, [ready, open, onAfterOpen]);

  return null; // This component renders nothing; it just triggers the Plaid Link flow.
};

const PlaidLink = ({ data, isConnected, disabled, onConnectedCallback }) => {
  const { linkToken, setPublicTokenHandled, generateLinkToken, handlePublicToken } = usePlaidLinkToken(data.t.lang);
  const [launchRequested, setLaunchRequested] = useState(false);
  const [opening, setOpening] = useState(false);

  const onSuccess = useCallback(
    (public_token, metadata) => {
      handlePublicToken(public_token, metadata);
      // After success we can reset state if desired.
      if (onConnectedCallback) onConnectedCallback();
      setLaunchRequested(false);
      setOpening(false);
    },
    [handlePublicToken, onConnectedCallback]
  );

  const createConnexion = useCallback(() => {
    // Reset any previous public token handled flag (if used elsewhere)
    setPublicTokenHandled(false);
    setLaunchRequested(true);
    setOpening(true);
    // Always generate a fresh link token on each intent (Plaid tokens are single-use)
    generateLinkToken();
  }, [generateLinkToken, setPublicTokenHandled]);

  const label = isConnected ? data.t.read('cc_transactions.reconnect_bank') : data.t.read('cc_transactions.connect_bank');

  return (
    <>
      <button
        className='btn btn-primary plaid-link-button'
        onClick={createConnexion}
        disabled={opening && !linkToken || disabled}
        data-testid='plaid-link-button'
        type='button'
      >
        <FontAwesomeIcon icon={faUniversity} data-testid='plaid-link-icon' />
        {opening && !linkToken ? <LoadingSpinner size='sm' /> : label}
      </button>
      {launchRequested && linkToken && (
        <PlaidLinkFlowLauncher
          token={linkToken}
          onSuccess={onSuccess}
          onAfterOpen={() => setOpening(false)}
        />
      )}
    </>
  );
};

export default inject('data')(PlaidLink);
